import { FC, useState } from 'react';
import cn from 'classnames';
import Link from 'next/link';
import { isMobile } from 'react-device-detect';
// Types
import { ContactType, TargetType } from 'api/graphql-global-types';
// Helpers
import {
  getMentionLabel,
  sortByRegistered,
  MentionData,
} from 'helpers/mentions';
// Components
import MentionsList from './MentionsListModal/MentionsList';
// UI
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
// Styles
import styles from './MentionsView.module.scss';

type MentionSize = 'md' | 'sm';

type MentionsViewProps = {
  mentions: MentionData[];
  className?: string;
  size?: MentionSize;
};

const MentionsView: FC<MentionsViewProps> = ({
  mentions,
  className,
  size = 'md',
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Sort mentions: REGISTERED first
  const sortedMentions = [...mentions].sort(sortByRegistered);

  // Get the first mentions: 2 on mobile, 5 on desktop
  const maxMentionsToShow = isMobile ? 2 : 5;
  const firstMentions = sortedMentions.slice(0, maxMentionsToShow);

  const totalMentions = mentions.length;
  const remainingMentions = totalMentions - firstMentions.length;
  const remainingMentionsText = ` and ${remainingMentions} more`;

  // Determine which mentions to display
  const displayedMentions = isMobile
    ? firstMentions // On mobile, always show only first mentions (list doesn't expand on mobile)
    : isExpanded
    ? sortedMentions
    : firstMentions;

  const handleExpandClick = () => {
    if (isMobile) {
      setIsModalOpen(true);
    } else {
      setIsExpanded(true);
    }
  };

  const handleCollapseClick = () => {
    setIsExpanded(false);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  return (
    <div
      className={cn(styles.mentionList, className, {
        [styles.smallMentions]: size === 'sm',
      })}
    >
      {!!firstMentions.length && !isMobile && (
        // desktop view
        <div className={styles.mentionHeader}>
          <p className={styles.mentionLabel}>Mentions</p>
          <p className={styles.mentionCount}>{totalMentions} mentions</p>
        </div>
      )}
      {isMobile && !!firstMentions.length && (
        // mobile view
        <div className={styles.mobileHeader}>
          <div className={styles.mentionHeader}>
            <p className={styles.mentionLabel}>Mentions</p>
            <p className={styles.mentionCount}>{totalMentions} mentions</p>
          </div>
          {totalMentions >= 3 && (
            <button
              type="button"
              onClick={() => setIsModalOpen(true)}
              className={styles.seeAllButton}
            >
              See All
              <MillionsIcon
                name="chevronRight"
                size={16}
                className={styles.arrowIcon}
              />
            </button>
          )}
        </div>
      )}
      <div className={styles.mentionViewItems}>
        {displayedMentions.map((mention) => (
          <div
            key={mention.id}
            className={cn(styles.mentionItem, {
              [styles.darkTheme]:
                mention.targetType === TargetType.UNREGISTERED,
            })}
          >
            {mention.targetType === TargetType.REGISTERED &&
            mention.store?.slug ? (
              <Link href={`/${mention.store.slug}`}>
                <a className={styles.linkWrapper}>
                  <img
                    src={
                      mention.store?.storeDetails?.avatarURL || '/favicon.png'
                    }
                    alt="mention avatar"
                    className={styles.imageAvatar}
                  />
                  <p className={styles.mentionTitle}>
                    {getMentionLabel(mention)}
                  </p>
                </a>
              </Link>
            ) : (
              <>
                {/* For brand mentions remove links at the moment */}
                {mention.unregisteredContact?.url &&
                mention.unregisteredContact?.type !== ContactType.BRAND ? (
                  <Link
                    href={
                      mention.unregisteredContact.url.startsWith('http')
                        ? mention.unregisteredContact.url
                        : `https://${mention.unregisteredContact.url}`
                    }
                  >
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.linkWrapper}
                    >
                      <img
                        src={
                          mention.store?.storeDetails?.avatarURL ||
                          '/favicon.png'
                        }
                        alt="mention avatar"
                        className={cn(styles.imageAvatar, {
                          [styles.hideAvatarImage]:
                            mention.targetType === TargetType.UNREGISTERED,
                        })}
                      />
                      <p className={styles.mentionTitle}>
                        {getMentionLabel(mention)}
                      </p>
                    </a>
                  </Link>
                ) : (
                  <div className={styles.disabledLink}>
                    <img
                      src={
                        mention.store?.storeDetails?.avatarURL || '/favicon.png'
                      }
                      alt="mention avatar"
                      className={cn(styles.imageAvatar, {
                        [styles.hideAvatarImage]:
                          mention.targetType === TargetType.UNREGISTERED,
                      })}
                    />
                    <p className={styles.mentionTitle}>
                      {getMentionLabel(mention)}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        ))}
        {!isExpanded && remainingMentions > 0 && (
          <button
            type="button"
            onClick={handleExpandClick}
            className={styles.expandButton}
          >
            {remainingMentionsText}
          </button>
        )}
        {isExpanded && !isMobile && (
          <button
            type="button"
            onClick={handleCollapseClick}
            className={styles.expandButton}
          >
            Show Less
          </button>
        )}
      </div>
      {isModalOpen && (
        <MentionsList mentions={mentions} onClose={handleCloseModal} />
      )}
    </div>
  );
};

export default MentionsView;
